// Cart and shipping configuration constants
// These should match the fallback logic in shipping-rates edge function

/** Base shipping cost for first 500g (in INR) */
export const SHIPPING_BASE_COST = 40;

/** Additional cost per 500g slab (in INR) */
export const SHIPPING_PER_SLAB_COST = 20;

/** Estimated weight per book for shipping calculations (in kg) */
export const WEIGHT_PER_BOOK_KG = 0.5;

/**
 * Route distance multiplier — MUST stay identical to
 * `calculateFallbackShipping` in the `razorpay-create-order` and
 * `shipping-rates` edge functions, otherwise the page estimate and the
 * payment-time estimate disagree (the old ₹60 vs ₹84 mismatch).
 * Same first 3 digits = local, same first digit = zonal, else national.
 */
export const shippingDistanceMultiplier = (originPincode?: string, destPincode?: string): number => {
  const o = (originPincode ?? '').trim();
  const d = (destPincode ?? '').trim();
  if (!/^\d{6}$/.test(o) || !/^\d{6}$/.test(d)) return 1;
  if (o.slice(0, 3) === d.slice(0, 3)) return 1;
  return o[0] === d[0] ? 1.4 : 1.8;
};

/**
 * Fallback (estimated) shipping cost. Deterministic: identical inputs always
 * produce the identical amount, so recalculating cannot make the total drift.
 * When either pincode is unknown the multiplier is 1 (cheapest, never
 * over-charges) — the charge is labelled an estimate either way.
 */
export const calculateShippingCost = (
  bookCount: number,
  originPincode?: string,
  destPincode?: string,
): number => {
  const safeCount = Math.max(1, Math.round(bookCount));
  const weight = safeCount * WEIGHT_PER_BOOK_KG;
  const slabs = Math.ceil(weight / WEIGHT_PER_BOOK_KG);
  const base = SHIPPING_BASE_COST + (slabs - 1) * SHIPPING_PER_SLAB_COST;
  return Math.round(base * shippingDistanceMultiplier(originPincode, destPincode));
};

